import { User } from "lucide-react";
import { useEffect, useState } from "react";
import { useSetting } from "../../controllers/SettingsContext";

interface BasicInfoFormProps {
  type?: "company" | "individual";
  companyId?: string;
}

export function BasicInfoForm({ type = "company" }: BasicInfoFormProps) {
  const {
    companyLogo,
    setCompanyLogo,
    companyName,
    setCompanyName,
    aboutDescription,
    setAboutDescription,
    hasOtherBranch,
    setHasOtherBranch,
  } = useSetting();

  const [logoError, setLogoError] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [branchEnabled, setBranchEnabled] = useState(true);
  const isCompany = type === "company";

  useEffect(() => {
    setBranchEnabled(Boolean(hasOtherBranch));
  }, [hasOtherBranch]);

  const displayImage =
    logoPreview || (typeof companyLogo === "string" ? companyLogo : null);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCompanyLogo(file);
      setLogoError(false);
      const objectUrl = URL.createObjectURL(file);
      setLogoPreview(objectUrl);
    }
  };

  const handleBranchToggle = () => {
    const newValue = !branchEnabled;
    setBranchEnabled(newValue);
    setHasOtherBranch(newValue);
  };

  return (
    <div className="space-y-6">
      {/* Beautiful horizontal banner logo upload */}
      <div className="relative w-full h-48 rounded-2xl overflow-hidden shadow-lg">
        {/* background image — full cover */}
        {displayImage && !logoError ? (
          <img
            src={displayImage}
            alt="Company Banner"
            onError={() => setLogoError(true)}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900 via-blue-700 to-blue-500" />
        )}

        {/* dark glass overlay */}
        <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]" />

        {/* centered content on top of overlay */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full gap-2">
          {displayImage && !logoError ? (
            <img
              src={displayImage}
              alt="Logo"
              onError={() => setLogoError(true)}
              className="w-20 h-20 rounded-full object-cover border-[3px] border-white shadow-xl"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-white/20 border-2 border-white/50 flex items-center justify-center">
              <User className="w-10 h-10 text-white" />
            </div>
          )}

          <label className="cursor-pointer bg-white/20 hover:bg-white/40 border border-white/60 text-white text-xs font-semibold px-5 py-1.5 rounded-full backdrop-blur-sm transition-all duration-200 shadow-md">
            {displayImage ? "Change Image" : "Upload Image"}
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* name */}
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            {isCompany ? "Company Name" : "Company Name"}
          </label>
          <input
            type="text"
            value={companyName || ""}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder={isCompany ? "Enter Company Name" : "Enter Company Name"}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* about */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {isCompany ? "About Company" : "About Company"}
        </label>
        <textarea
          value={aboutDescription || ""}
          onChange={(e) => setAboutDescription(e.target.value)}
          placeholder={isCompany ? "Enter company details" : "Enter personal details"}
          rows={3}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      </div>

      {/*  Fixed branch toggle */}
      <div className="flex items-center justify-between pt-4">
        <span className="text-sm font-medium">Do you have a branch?</span>
        <button
          type="button"
          onClick={handleBranchToggle}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
            branchEnabled ? "bg-blue-500" : "bg-gray-200"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
              branchEnabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </div>
  );
}